import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShoppingBag, Menu, X, Globe, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useStore } from "../context/StoreContext";
import { useLang, LANGS } from "../context/LanguageContext";
import { useSiteContent } from "../hooks/useSiteContent";

export default function Nav() {
  const { cartCount, setCartOpen, settings } = useStore();
  const { lang, setLang, t } = useLang();
  const { img, num } = useSiteContent();
  const logo = img("logo_url", "");
  const logoSize = num("logo_size", 22);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setLangOpen(false); }, [loc.pathname]);

  const links = [
    { to: "/equipos", label: t("nav.equipos") },
    { to: "/software", label: t("nav.software") },
    { to: "/consultoria", label: t("nav.consultoria") },
    { to: "/catalogo", label: t("nav.catalogo") },
  ];

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${scrolled ? "backdrop-blur-xl bg-[color:var(--bg)]/80 border-b border-ink/10 py-3" : "py-5 border-b border-transparent"}`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          {logo ? (
            <img src={logo} alt={settings.brand_name} style={{ height: `${logoSize}px` }} className="w-auto object-contain" />
          ) : (
            <>
              <span className="w-2.5 h-2.5 bg-gold rotate-45 group-hover:rotate-[135deg] transition-transform duration-500" />
              <span className="font-display font-extrabold tracking-tight" style={{ fontSize: `${logoSize}px` }}>{settings.brand_name}</span>
            </>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-link-${l.to.slice(1)}`}
              className="text-sm font-medium relative group py-1">
              {l.label}
              <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-gold group-hover:w-full transition-[width] duration-300" />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative">
            <button onClick={() => setLangOpen((v) => !v)} data-testid="lang-switcher"
              className="flex items-center gap-1.5 px-3 py-2 border border-ink/15 hover:border-gold hover:text-gold transition-colors text-sm">
              <Globe className="w-4 h-4" /> {LANGS.find((l) => l.code === lang)?.label} <ChevronDown className="w-3 h-3" />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  className="absolute right-0 mt-2 w-36 bg-[color:var(--bg)] border border-ink/10 shadow-xl z-50">
                  {LANGS.map((l) => (
                    <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }} data-testid={`lang-${l.code}`}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-surface transition-colors ${lang === l.code ? "text-gold font-medium" : ""}`}>
                      {l.name}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button data-testid="open-cart-btn" onClick={() => setCartOpen(true)}
            className="relative flex items-center gap-2 px-4 py-2 border border-ink/15 hover:border-gold hover:text-gold transition-colors duration-300">
            <ShoppingBag className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">{t("nav.carrito")}</span>
            {cartCount > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-gold text-inverse text-xs font-bold w-5 h-5 flex items-center justify-center font-mono">{cartCount}</span>
            )}
          </button>
          <button className="md:hidden p-2" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-btn">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[color:var(--bg)] border-t border-ink/10">
            <div className="px-6 py-4 flex flex-col gap-4">
              {links.map((l) => (
                <Link key={l.to} to={l.to} className="text-lg font-display font-medium">{l.label}</Link>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
